import { SendsarClient } from "./client.js";
import type { SessionResponse } from "./types.js";

export type SessionManagerStatus = "idle" | "loading" | "ready" | "offline" | "error";

export type SessionManagerState = {
  status: SessionManagerStatus;
  client: SendsarClient | null;
  session: SessionResponse | null;
  error?: string;
};

export type ParseSessionResult =
  | { ok: true; session: SessionResponse }
  | { ok: false; status: "offline" | "error"; error?: string };

const DEFAULT_REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/** Milliseconds until the session token should be refreshed (0 or negative = refresh now). */
export function msUntilTokenRefresh(
  expiresAt: string,
  refreshBeforeExpiryMs: number,
  now = Date.now(),
): number {
  return new Date(expiresAt).getTime() - now - refreshBeforeExpiryMs;
}

/** Default parser for `fetch()` session responses (503 offline, 401 unauthorized). */
export async function parseSessionResponse(response: Response): Promise<ParseSessionResult> {
  if (response.status === 503) {
    return { ok: false, status: "offline" };
  }
  if (response.status === 401) {
    return {
      ok: false,
      status: "error",
      error: "Unauthorized — log in to use chat",
    };
  }
  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    return {
      ok: false,
      status: "error",
      error: (errBody as { error?: string }).error ?? "Failed to connect to Sendsar",
    };
  }
  const session = (await response.json()) as SessionResponse;
  return { ok: true, session };
}

export type CreateSessionManagerOptions = {
  /** Fetch session from your backend — return a `Response` or parsed `SessionResponse`. */
  fetchSession: () => Promise<Response | SessionResponse>;
  refreshBeforeExpiryMs?: number;
  /** Disconnect WebSocket on `pagehide` (default true in browser). */
  disconnectOnPageHide?: boolean;
  parseSession?: (response: Response) => Promise<ParseSessionResult>;
  onStateChange?: (state: SessionManagerState) => void;
};

export type SessionManager = {
  getState(): SessionManagerState;
  start(): Promise<void>;
  stop(): Promise<void>;
  restart(): Promise<void>;
};

export function createSessionManager(options: CreateSessionManagerOptions): SessionManager {
  const refreshBeforeExpiryMs =
    options.refreshBeforeExpiryMs ?? DEFAULT_REFRESH_BEFORE_EXPIRY_MS;
  const disconnectOnPageHide = options.disconnectOnPageHide ?? true;
  const parseSession = options.parseSession ?? parseSessionResponse;

  let state: SessionManagerState = {
    status: "idle",
    client: null,
    session: null,
  };
  let client: SendsarClient | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let removePageHide: (() => void) | null = null;
  let connectGen = 0;
  let cancelled = false;

  function setState(next: SessionManagerState) {
    state = next;
    options.onStateChange?.(state);
  }

  async function resolveSession(raw: Response | SessionResponse): Promise<ParseSessionResult> {
    if (raw instanceof Response) {
      return parseSession(raw);
    }
    return { ok: true, session: raw };
  }

  async function refreshSessionToken(activeClient: SendsarClient): Promise<void> {
    try {
      const parsed = await resolveSession(await options.fetchSession());
      if (!parsed.ok || cancelled) {
        if (!parsed.ok && parsed.status === "error" && !cancelled && state.status === "ready") {
          setState({
            ...state,
            status: "error",
            error: parsed.error ?? "Session refresh failed",
          });
        }
        return;
      }
      await activeClient.connect({
        userId: parsed.session.chatUserId,
        token: parsed.session.token,
      });
      if (cancelled) return;
      if (state.status === "ready" && state.session) {
        setState({
          ...state,
          session: {
            ...state.session,
            token: parsed.session.token,
            expiresAt: parsed.session.expiresAt,
            ...(parsed.session.chatSettings
              ? { chatSettings: parsed.session.chatSettings }
              : {}),
          },
        });
      }
      scheduleTokenRefresh(activeClient, parsed.session);
    } catch (err) {
      if (!cancelled && state.status === "ready") {
        setState({
          ...state,
          status: "error",
          error: err instanceof Error ? err.message : "Session refresh failed",
        });
      }
    }
  }

  function scheduleTokenRefresh(activeClient: SendsarClient, session: SessionResponse) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;

    const delay = msUntilTokenRefresh(session.expiresAt, refreshBeforeExpiryMs);
    if (delay <= 0) {
      void refreshSessionToken(activeClient);
      return;
    }

    refreshTimer = setTimeout(() => {
      void refreshSessionToken(activeClient);
    }, delay);
  }

  function attachPageHide(activeClient: SendsarClient) {
    if (!disconnectOnPageHide || typeof window === "undefined") return;
    const onPageHide = () => {
      void activeClient.disconnect();
    };
    window.addEventListener("pagehide", onPageHide);
    removePageHide = () => window.removeEventListener("pagehide", onPageHide);
  }

  async function connectSession() {
    const gen = ++connectGen;
    setState({ status: "loading", client: null, session: null });
    try {
      const parsed = await resolveSession(await options.fetchSession());
      if (parsed.ok === false && parsed.status === "offline") {
        if (!cancelled) {
          setState({ status: "offline", client: null, session: null });
        }
        return;
      }
      if (parsed.ok === false) {
        if (!cancelled) {
          setState({
            status: "error",
            client: null,
            session: null,
            error: parsed.error ?? "Sendsar connection failed",
          });
        }
        return;
      }
      const payload = parsed.session;
      const nextClient = new SendsarClient({ apiUrl: payload.apiUrl });
      await nextClient.connect({
        userId: payload.chatUserId,
        token: payload.token,
      });
      if (cancelled || gen !== connectGen) {
        await nextClient.disconnect();
        return;
      }
      client = nextClient;
      setState({ status: "ready", client: nextClient, session: payload });
      scheduleTokenRefresh(nextClient, payload);
      attachPageHide(nextClient);
    } catch (err) {
      if (!cancelled) {
        setState({
          status: "error",
          client: null,
          session: null,
          error: err instanceof Error ? err.message : "Sendsar connection failed",
        });
      }
    }
  }

  function cleanup() {
    cancelled = true;
    connectGen += 1;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    removePageHide?.();
    removePageHide = null;
  }

  return {
    getState: () => state,
    async start() {
      cancelled = false;
      await connectSession();
    },
    async stop() {
      cleanup();
      const active = client;
      client = null;
      await active?.disconnect();
      setState({ status: "idle", client: null, session: null });
    },
    async restart() {
      await this.stop();
      cancelled = false;
      await connectSession();
    },
  };
}
